import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';

/**
 * Shared permission helpers for the Roles & Permissions UI.
 *
 * Permission codes follow the shape `action.resource.scope`
 * (e.g. `write.settings.company`). These helpers are the single source of
 * truth for parsing codes, ordering/labelling actions, and labelling scopes,
 * and are consumed by both the read-only RoleDisplay matrix and the editable
 * PermissionTable.
 */

// Action -> column display label
export const ACTION_DISPLAY_MAP: Record<string, string> = {
  read: 'View',
  write: 'Create/Edit',
  delete: 'Delete',
  export: 'Export',
  show: 'Show',
};

// Action -> sort priority (lower renders first)
export const ACTION_PRIORITY: Record<string, number> = {
  read: 1,
  write: 2,
  delete: 3,
  show: 10,
  export: 20,
};

/**
 * Extract resource and scope from a permission code (`action.resource.scope`).
 */
export const getResourceAndScope = (
  code: string
): { resource: string; scope: string } => {
  const parts = code.split('.');
  if (parts.length < 3) return { resource: '', scope: '' };

  return {
    resource: parts[1],
    scope: parts[2],
  };
};

/**
 * Extract the action (first segment) from a permission code.
 */
export const getAction = (code: string): string => {
  const parts = code.split('.');
  if (parts.length < 1) return '';

  return parts[0];
};

/**
 * Build a permission code from action, resource, and scope.
 */
export const createPermissionCode = (
  action: string,
  resource: string,
  scope: string
): string => `${action}.${resource}.${scope}`;

/**
 * Human-readable display name for a scope.
 */
export const getScopeDisplayName = (scope: string): string => {
  const scopeMap: { [key: string]: string } = {
    own: 'Personal', // Changed from 'Own' to 'Personal'
    personal: 'Personal',
    company: 'Company',
    bounded: 'Bounded',
    unbounded: 'Unbounded',
    direct_report: 'Direct Reports',
    site_providers: 'Site Providers',
    all: 'All',
  };

  return (
    scopeMap[scope] ||
    scope
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
};

/**
 * Format a resource segment for inline display, sentence-cased
 * (e.g. `filter_sets` -> `Filter sets`, `providers_in_territory` ->
 * `Providers in territory`). Used for export resource rows.
 */
export const formatResourceName = (resource: string): string =>
  resource.charAt(0).toUpperCase() + resource.slice(1).replace(/_/g, ' ');

/**
 * Title-case a resource segment for a section heading
 * (e.g. `filter_sets` -> `Filter Sets`).
 */
export const formatSectionLabel = (resource: string): string =>
  resource
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Display text for an action column header.
 */
export const getActionDisplayText = (action: string): string =>
  ACTION_DISPLAY_MAP[action] || sentenceCase(action);

/**
 * Section labels that should not derive from `formatResourceName`.
 */
const SECTION_LABEL_OVERRIDES: Record<string, string> = {
  territory: 'Territory Access',
};

// All `export.*` permissions collapse into a single section.
const EXPORT_GROUP_KEY = 'export';
const EXPORT_GROUP_LABEL = 'Export Access';

export interface RoleDisplayRow {
  /** Resource segment used to rebuild the permission code for cell checks. */
  resource: string;
  /** Scope segment used to rebuild the permission code for cell checks. */
  scope: string;
  /** Primary label shown for the row. */
  label: string;
  /** Optional helper text shown beneath the label. */
  subLabel?: string;
}

export interface RoleDisplayGroup {
  /** Stable key for the section. */
  key: string;
  /** Section label. */
  group: string;
  /** Column actions, ordered by ACTION_PRIORITY. */
  actions: string[];
  /** Rows (scopes, or export resources) in first-seen order. */
  rows: RoleDisplayRow[];
}

/**
 * Resolve the inline description shown beneath a single-action row's label.
 *
 * Only used for single-action groups (Exports, Show, read-only), where a row
 * maps to exactly one permission so the description is unambiguous. Multi-action
 * rows surface each permission's description per-cell instead (see
 * PermissionMatrix), since one row spans several codes. Uses the catalog
 * description for the row's single code, with a generated fallback.
 */
const resolveRowDescription = (
  row: { resource: string; scope: string },
  action: string,
  descriptions: Record<string, string>,
  isExportGroup: boolean
): string => {
  const description =
    descriptions[createPermissionCode(action, row.resource, row.scope)];
  if (description) return description;

  const resourceLabel = formatResourceName(row.resource).toLowerCase();

  return isExportGroup
    ? `Export ${resourceLabel} data`
    : `${getScopeDisplayName(row.scope)} ${resourceLabel}`;
};

/**
 * Build the read-only display matrix from a set of permission codes.
 *
 * Groups are keyed by resource (all `export.*` collapse into one "Export
 * Access" section). Within a group, rows are the distinct scopes and columns
 * are the union of actions present across those scopes — so a resource that
 * only has `write`/`delete` (e.g. settings) renders without a View column,
 * and a scope missing an action its siblings have renders a denied cell.
 *
 * `descriptions` maps permission code -> human description (from the catalog);
 * each row shows the description of its most representative code, falling back
 * to a generated one. Order follows first appearance in the permission list.
 */
export const buildRoleDisplayGroups = (
  permissions: string[],
  descriptions: Record<string, string> = {}
): RoleDisplayGroup[] => {
  const groupMap = new Map<
    string,
    {
      key: string;
      group: string;
      actions: Set<string>;
      rows: Map<string, RoleDisplayRow>;
    }
  >();

  permissions.forEach((permission) => {
    const [action, resource, scope] = permission.split('.');
    if (!action || !resource || !scope) return;

    const isExport = action === 'export';
    const groupKey = isExport ? EXPORT_GROUP_KEY : resource;
    const groupLabel = isExport
      ? EXPORT_GROUP_LABEL
      : SECTION_LABEL_OVERRIDES[resource] || formatSectionLabel(resource);

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        key: groupKey,
        group: groupLabel,
        actions: new Set<string>(),
        rows: new Map<string, RoleDisplayRow>(),
      });
    }

    const group = groupMap.get(groupKey);
    if (!group) return;

    group.actions.add(action);

    // Rows are keyed by resource+scope so they stay unique across sections
    // (the same scope, e.g. `company`, appears in many resources).
    const rowKey = `${resource}.${scope}`;

    if (!group.rows.has(rowKey)) {
      group.rows.set(rowKey, {
        resource,
        scope,
        label: isExport
          ? formatResourceName(resource)
          : getScopeDisplayName(scope),
      });
    }
  });

  return Array.from(groupMap.values()).map((group) => {
    const actions = Array.from(group.actions).sort(
      (a, b) => (ACTION_PRIORITY[a] ?? 99) - (ACTION_PRIORITY[b] ?? 99)
    );
    const isExportGroup = group.key === EXPORT_GROUP_KEY;
    // A single-action group's rows map to exactly one permission, so an inline
    // description is unambiguous. Multi-action rows describe per-cell instead.
    const isSingleAction = actions.length === 1;

    return {
      key: group.key,
      group: group.group,
      actions,
      rows: Array.from(group.rows.values()).map((row) => ({
        ...row,
        subLabel: isSingleAction
          ? resolveRowDescription(row, actions[0], descriptions, isExportGroup)
          : undefined,
      })),
    };
  });
};
